import { useEffect, useState } from "react";
import { Crown, Globe, TrendingUp, Users, Car, ShieldCheck, Activity, AlertTriangle, ShoppingCart, Award, Cpu, RefreshCw, FileCheck2, Gavel, CheckCircle2, XCircle, Pause, FileText } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const fmtEur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso || "—"; } };

export default function SuperAdmin() {
  const [tab, setTab] = useState("overview");
  const [kpi, setKpi] = useState(null);
  const [cartLog, setCartLog] = useState({ items: [], kpi: {} });
  const [yard, setYard] = useState({ leaderboard: [] });
  const [obd, setObd] = useState([]);
  const [kycList, setKycList] = useState({ items: [], counts: {} });
  const [disputes, setDisputes] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [k, c, y, o, kyc, disp] = await Promise.all([
        api.get("/v3/superadmin/global-kpi"),
        api.get("/v3/cart/recovery/log?limit=30"),
        api.get("/v3/yard/leaderboard?days=30"),
        api.get("/v3/obd/alerts?limit=20"),
        api.get("/governance/kyc/list?limit=50"),
        api.get("/governance/dispute/list?limit=50"),
      ]);
      setKpi(k.data || null);
      setCartLog(c.data || { items: [], kpi: {} });
      setYard(y.data || { leaderboard: [] });
      setObd(o.data?.items || []);
      setKycList(kyc.data || { items: [], counts: {} });
      setDisputes(disp.data || { items: [], counts: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento dashboard globale");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const reviewKyc = async (kyc_id, decision, reason) => {
    setBusyId(kyc_id);
    try {
      await api.post("/governance/kyc/review", { kyc_id, decision, reason });
      toast.success(`KYC ${decision}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusyId(null); }
  };

  const resolveDispute = async (dispute_id, resolution, refund_amount_eur, notes) => {
    setBusyId(dispute_id);
    try {
      await api.post("/governance/dispute/resolve", { dispute_id, resolution, refund_amount_eur: Number(refund_amount_eur) || 0, notes });
      toast.success(`Dispute ${resolution}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusyId(null); }
  };

  const n = kpi?.network || {};
  const partners = kpi?.partners || [];

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
            <Crown className="h-9 w-9 text-amber-400" />
            Super Admin <span className="text-amber-400">Network</span>
          </h1>
          <p className="text-slate-400 text-sm mt-1">Vista globale aggregata su tutti i partner FabriGroup. Royalty, KPI, dispute, gamification.</p>
        </div>
        <button onClick={loadAll} disabled={loading} className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 flex items-center gap-2 text-sm" data-testid="superadmin-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </button>
      </div>

      {/* Network KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Partner Attivi" value={n.tenants ?? "—"} icon={Globe} accent="text-cyan-400" testid="kpi-tenants" />
        <Kpi label="Flotta Totale" value={n.cars_total ?? "—"} sub={`${n.cars_rented ?? 0} noleggiate`} icon={Car} accent="text-emerald-400" testid="kpi-cars" />
        <Kpi label="GMV Network" value={fmtEur(n.gmv_total_eur)} icon={TrendingUp} accent="text-fuchsia-400" testid="kpi-gmv" />
        <Kpi label="Royalty FabriGroup" value={fmtEur(n.royalty_total_eur)} sub={`${Math.round((n.royalty_pct || 0) * 100)}% commission`} icon={Crown} accent="text-amber-400" testid="kpi-royalty" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Clienti" value={n.customers_total ?? "—"} sub={`${n.blacklist_total ?? 0} blacklist`} icon={Users} accent="text-blue-400" testid="kpi-customers" />
        <Kpi label="Eventi Compliance 30d" value={n.compliance_events_30d ?? "—"} icon={ShieldCheck} accent="text-emerald-400" testid="kpi-compliance" />
        <Kpi label="Dispute Pending" value={n.disputes_pending ?? 0} icon={AlertTriangle} accent={n.disputes_pending > 0 ? "text-rose-400" : "text-slate-400"} testid="kpi-disputes" />
        <Kpi label="Cart Abandon Rate 30d" value={`${cartLog.kpi?.abandon_rate_pct ?? 0}%`} sub={`${cartLog.kpi?.abandoned_30d ?? 0} / ${cartLog.kpi?.carts_30d ?? 0}`} icon={ShoppingCart} accent="text-amber-400" testid="kpi-abandon" />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-800">
        {[
          { id: "overview", label: "Network Partners", icon: Globe },
          { id: "cart", label: "Cart Recovery", icon: ShoppingCart },
          { id: "yard", label: "Yard Leaderboard", icon: Award },
          { id: "obd", label: "OBD Alerts", icon: Cpu },
          { id: "kyc", label: `KYC (${kycList.counts?.pending || 0})`, icon: FileCheck2 },
          { id: "disputes", label: `Dispute (${disputes.counts?.pending || 0})`, icon: Gavel },
        ].map((t) => {
          const Ico = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-all ${tab === t.id ? "border-amber-400 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}
              data-testid={`tab-${t.id}`}>
              <Ico className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/80 text-slate-400 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Partner / Tenant</th>
                <th className="text-right p-3">Auto</th>
                <th className="text-right p-3">Booking</th>
                <th className="text-right p-3">Confermati</th>
                <th className="text-right p-3">Contratti</th>
                <th className="text-right p-3">GMV</th>
                <th className="text-right p-3">Royalty</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 ? (
                <tr><td colSpan="7" className="text-center text-slate-500 py-10">Nessun partner registrato</td></tr>
              ) : partners.map((p) => (
                <tr key={p.tenant_id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`partner-${p.tenant_id}`}>
                  <td className="p-3 text-slate-200 font-medium">{p.tenant_id}</td>
                  <td className="p-3 text-right text-slate-300">{p.cars}</td>
                  <td className="p-3 text-right text-slate-300">{p.bookings}</td>
                  <td className="p-3 text-right text-emerald-300">{p.confirmed}</td>
                  <td className="p-3 text-right text-slate-300">{p.contracts}</td>
                  <td className="p-3 text-right text-fuchsia-300 font-semibold">{fmtEur(p.gmv_eur)}</td>
                  <td className="p-3 text-right text-amber-300 font-semibold">{fmtEur(p.royalty_eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "cart" && (
        <div className="space-y-3">
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-sm text-slate-300">
            Worker automatico: ogni carrello in stato <b>select</b> o <b>checkout_started</b> non chiuso entro 15 minuti viene marcato come <b>abandoned</b> e accodato per il recovery (sconto -5% franchigia). Quando integrerai il gateway WhatsApp, il messaggio partirà in automatico — ora viene loggato.
          </div>
          {cartLog.items.length === 0 ? (
            <div className="text-center py-12 text-slate-500 border border-dashed border-slate-800 rounded-xl">Nessun evento di recovery registrato.</div>
          ) : cartLog.items.map((c) => (
            <div key={c.id} className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm" data-testid={`cart-${c.id}`}>
              <div className="flex-1">
                <div className="text-slate-200 font-medium">{c.customer_email || "anonimo"} <span className="text-slate-500">· {c.city || "—"}</span></div>
                <div className="text-xs text-slate-400 mt-0.5">{c.message}</div>
              </div>
              <div className="flex flex-col sm:items-end gap-1">
                <span className="text-amber-300 font-semibold">{fmtEur(c.amount)} · sconto {c.discount_offered_pct}%</span>
                <span className="text-xs text-slate-500">{fmtDate(c.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "yard" && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/80 text-slate-400 text-xs uppercase">
              <tr>
                <th className="text-left p-3">#</th>
                <th className="text-left p-3">Operatore</th>
                <th className="text-right p-3">Task completati</th>
                <th className="text-right p-3">Durata media (min)</th>
                <th className="text-right p-3">Best (min)</th>
                <th className="text-right p-3">Score</th>
              </tr>
            </thead>
            <tbody>
              {yard.leaderboard.length === 0 ? (
                <tr><td colSpan="6" className="text-center text-slate-500 py-10">Nessun dato di gamification ancora.</td></tr>
              ) : yard.leaderboard.map((o, i) => (
                <tr key={o.operator_id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`yard-row-${o.operator_id}`}>
                  <td className="p-3 text-slate-400">{i + 1}{i === 0 && " 🏆"}{i === 1 && " 🥈"}{i === 2 && " 🥉"}</td>
                  <td className="p-3 text-slate-200 font-medium">{o.operator_name || o.operator_id}</td>
                  <td className="p-3 text-right text-slate-300">{o.tasks}</td>
                  <td className="p-3 text-right text-cyan-300">{o.avg_duration_min}</td>
                  <td className="p-3 text-right text-emerald-300">{o.best_duration_min}</td>
                  <td className="p-3 text-right">
                    <span className="px-2 py-1 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 font-semibold">{o.gamification_score}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "obd" && (
        <div className="space-y-2">
          {obd.length === 0 ? (
            <div className="text-center py-12 text-slate-500 border border-dashed border-slate-800 rounded-xl">
              Nessun alert OBD ricevuto. Endpoint hardware: <code className="text-cyan-400">POST /api/v3/obd/telemetry</code>
            </div>
          ) : obd.map((a) => (
            <div key={a.id} className="bg-slate-900/60 border border-rose-500/30 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm" data-testid={`obd-${a.id}`}>
              <div>
                <div className="text-rose-300 font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4" />OBD anomaly: {a.fault_codes?.join(", ")}</div>
                <div className="text-xs text-slate-400 mt-0.5">car_id: <code>{a.car_id}</code></div>
              </div>
              <div className="flex flex-col sm:items-end">
                <span className="text-cyan-300 font-medium">{a.reallocated_count} prenotazioni riallocate</span>
                <span className="text-xs text-slate-500">{fmtDate(a.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "kyc" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            {["pending", "in_review", "approved", "rejected", "suspended"].map((s) => (
              <div key={s} className="bg-slate-900/60 border border-slate-800 rounded-lg p-2 text-center" data-testid={`kyc-count-${s}`}>
                <div className="text-slate-400 uppercase">{s}</div>
                <div className="text-xl font-bold text-white">{kycList.counts?.[s] || 0}</div>
              </div>
            ))}
          </div>
          {kycList.items.length === 0 ? (
            <div className="text-center py-12 text-slate-500 border border-dashed border-slate-800 rounded-xl">
              Nessuna pratica KYC. Endpoint partner: <code className="text-cyan-400">POST /api/governance/kyc/submit</code>
            </div>
          ) : kycList.items.map((k) => (
            <div key={k.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid={`kyc-${k.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-white font-semibold">{k.company_name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded border ${
                      k.status === "approved" ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" :
                      k.status === "rejected" ? "bg-rose-500/10 border-rose-500/40 text-rose-300" :
                      k.status === "suspended" ? "bg-amber-500/10 border-amber-500/40 text-amber-300" :
                      k.status === "in_review" ? "bg-blue-500/10 border-blue-500/40 text-blue-300" :
                      "bg-slate-700/40 border-slate-600 text-slate-300"
                    }`}>{k.status}</span>
                    <code className="text-xs text-slate-500">{k.tenant_id}</code>
                  </div>
                  <div className="text-xs text-slate-400 mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                    <span>P.IVA: <b className="text-slate-200">{k.vat_number}</b></span>
                    <span>Città: <b className="text-slate-200">{k.operating_city || "—"}</b></span>
                    <span>Email: <b className="text-slate-200">{k.legal_email}</b></span>
                    <span>IBAN: <b className="text-slate-200 font-mono">{k.iban || "—"}</b></span>
                    <span>Contatto: <b className="text-slate-200">{k.contact_person || "—"}</b></span>
                    <span>Sottomessa: <b className="text-slate-200">{fmtDate(k.created_at)}</b></span>
                  </div>
                  {k.documents?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {k.documents.map((d, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-800 border border-slate-700 rounded text-slate-300 flex items-center gap-1">
                          <FileText className="h-3 w-3" /> {d.type} · {d.filename}
                        </span>
                      ))}
                    </div>
                  )}
                  {k.review_reason && (
                    <div className="mt-2 text-xs text-amber-300">Motivo review: {k.review_reason}</div>
                  )}
                </div>
                {!["approved", "rejected"].includes(k.status) && (
                  <div className="flex flex-col sm:flex-row gap-1.5">
                    <button disabled={busyId === k.id} onClick={() => reviewKyc(k.id, "approved", null)} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs flex items-center gap-1 disabled:opacity-50" data-testid={`kyc-approve-${k.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Approva
                    </button>
                    <button disabled={busyId === k.id} onClick={() => { const r = prompt("Motivo del rifiuto:"); if (r) reviewKyc(k.id, "rejected", r); }} className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs flex items-center gap-1 disabled:opacity-50" data-testid={`kyc-reject-${k.id}`}>
                      <XCircle className="h-3.5 w-3.5" /> Rifiuta
                    </button>
                    <button disabled={busyId === k.id} onClick={() => { const r = prompt("Motivo sospensione:"); if (r) reviewKyc(k.id, "suspended", r); }} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-xs flex items-center gap-1 disabled:opacity-50" data-testid={`kyc-suspend-${k.id}`}>
                      <Pause className="h-3.5 w-3.5" /> Sospendi
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "disputes" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            {["pending", "under_review", "resolved_customer", "resolved_partner", "rejected"].map((s) => (
              <div key={s} className="bg-slate-900/60 border border-slate-800 rounded-lg p-2 text-center" data-testid={`disp-count-${s}`}>
                <div className="text-slate-400 uppercase">{s.replace("_", " ")}</div>
                <div className="text-xl font-bold text-white">{disputes.counts?.[s] || 0}</div>
              </div>
            ))}
          </div>
          {disputes.items.length === 0 ? (
            <div className="text-center py-12 text-slate-500 border border-dashed border-slate-800 rounded-xl">
              Nessuna controversia aperta. Endpoint pubblico: <code className="text-cyan-400">POST /api/governance/dispute/open</code>
            </div>
          ) : disputes.items.map((d) => (
            <div key={d.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid={`disp-${d.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-white font-semibold">{d.reason}</span>
                    <span className={`text-xs px-2 py-0.5 rounded border ${
                      d.status === "resolved_customer" ? "bg-blue-500/10 border-blue-500/40 text-blue-300" :
                      d.status === "resolved_partner" ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" :
                      d.status === "rejected" ? "bg-rose-500/10 border-rose-500/40 text-rose-300" :
                      d.status === "under_review" ? "bg-amber-500/10 border-amber-500/40 text-amber-300" :
                      "bg-slate-700/40 border-slate-600 text-slate-300"
                    }`}>{d.status}</span>
                    <span className="text-rose-300 font-semibold">{fmtEur(d.amount_disputed_eur)}</span>
                    {d.refund_amount_eur > 0 && <span className="text-emerald-300">→ rimborso {fmtEur(d.refund_amount_eur)}</span>}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    <div>Cliente: <b className="text-slate-200">{d.customer_email}</b> {d.customer_phone && <>· <b>{d.customer_phone}</b></>}</div>
                    {d.booking_id && <div>Booking: <code className="text-slate-300">{d.booking_id}</code></div>}
                    <div>Tenant: <code className="text-slate-300">{d.tenant_id}</code> · aperta {fmtDate(d.created_at)}</div>
                  </div>
                  <div className="mt-2 text-sm text-slate-300 italic">&quot;{d.description}&quot;</div>
                  {d.resolution_notes && (
                    <div className="mt-2 text-xs text-emerald-300">Risoluzione: {d.resolution_notes}</div>
                  )}
                </div>
                {!["resolved_customer", "resolved_partner", "rejected"].includes(d.status) && (
                  <div className="flex flex-col gap-1.5">
                    <button disabled={busyId === d.id} onClick={() => {
                      const r = prompt(`Importo rimborso al cliente (€). Disputato: ${d.amount_disputed_eur}`, String(d.amount_disputed_eur));
                      if (r == null) return;
                      const n = prompt("Note risoluzione:");
                      if (!n) return;
                      resolveDispute(d.id, "resolved_customer", r, n);
                    }} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs flex items-center gap-1 disabled:opacity-50" data-testid={`disp-customer-${d.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Pro Cliente
                    </button>
                    <button disabled={busyId === d.id} onClick={() => {
                      const n = prompt("Note risoluzione (a favore del partner):");
                      if (!n) return;
                      resolveDispute(d.id, "resolved_partner", 0, n);
                    }} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs flex items-center gap-1 disabled:opacity-50" data-testid={`disp-partner-${d.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Pro Partner
                    </button>
                    <button disabled={busyId === d.id} onClick={() => {
                      const n = prompt("Motivo rigetto:");
                      if (!n) return;
                      resolveDispute(d.id, "rejected", 0, n);
                    }} className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs flex items-center gap-1 disabled:opacity-50" data-testid={`disp-reject-${d.id}`}>
                      <XCircle className="h-3.5 w-3.5" /> Rigetta
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Kpi = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      {Icon && <Icon className={`h-4 w-4 ${accent}`} />}
    </div>
    <div className={`text-2xl font-bold mt-2 ${accent}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);
